import { For, Match, Show, Switch } from "solid-js";
import { getRedemptionTransactionDetail } from "../../data/products";
import { clientRepo } from "~/server/apis/client_repo";
import { InitiateCoinPayoutResponse } from "~/server/types/payout";
import {
  RefundStatusType,
  TransactionDetailV4Response,
} from "../../data/transaction";
import { classNames } from "../../utils/etc";
import { toRupees } from "../../utils/number";
import { ThreeDotLoader } from "../../widgets/button";
import { ResourceLoader } from "../../widgets/resource_loader";
import { VoucherCardPlain } from "../voucher_edit/voucher_card";
import { PhosphorIcon } from "../../widgets/icons";
import {
  goBack,
  HubbleRoute,
  PageAlignment,
  pushPage,
} from "../../shared_states/modal";
import { VoucherGenSuccess } from "../payment_options/voucher_gen_success";
import { openIntercom } from "~/utils/third_party/intercom";
import {
  copyText,
  getCoinLabelCapitalized,
  getCoinLabelCapitalizedPluralized,
} from "~/utils/common";
import { useClassicContext } from "~/data/classic_context";
import { Divider } from "~/widgets/divider";
import {
  bankTransfer,
  defaultCoin,
  loadingIconBlack,
  rightArrowWhite,
} from "~/assets/assets";
import { Spacer } from "~/widgets/spacer";
import { DonationDetails, getDonationDetails } from "~/data/donation";
import { HighWaitingTimeBankTransfer } from "../bank_transfer/waiting_time_bank_transfer";
import { showHubbleBrandingOnVoucherCard } from "~/data/partner_config";

export type TransactionDetailType = "redemption" | "payout";

export function TransactionDetail(props: {
  transactionId: string;
  type: TransactionDetailType;
}) {
  const { isClientDonationType } = useClassicContext();
  const context = useClassicContext();
  const contextData = context.data;

  const getTransactionDetailData = async () => {
    let transactionDetail;
    if (props.type === "redemption") {
      transactionDetail = await getRedemptionTransactionDetail(
        props.transactionId
      );
      if (isClientDonationType()) {
        const donationDetails = await getDonationDetails(props.transactionId);
        return {
          transactionDetail,
          donationDetails,
          isPayout: false,
        };
      }
    } else {
      transactionDetail = await clientRepo.getPayoutById(props.transactionId);
    }
    return {
      transactionDetail,
      isPayout: props.type === "payout",
    };
  };

  const clientId =
    contextData.isLoggedIn && contextData.mode === "sdk"
      ? contextData.initParams.clientId
      : undefined;

  return (
    <ResourceLoader fetch={() => getTransactionDetailData()}>
      {(detail) => {
        const transactionDetail = detail.transactionDetail;
        return (
          <div
            class={`no-scrollbar absolute bottom-0 left-0 right-0 top-0 flex flex-col items-stretch gap-4 overflow-x-hidden overflow-y-scroll bg-baseTertiaryMedium p-5 `}
            classList={{
              "pt-[60px]": props.type === "redemption",
              "pt-5": props.type === "payout",
            }}
          >
            <PhosphorIcon
              name="arrow-left"
              class="absolute left-4 top-4 h-8 w-8  text-basePrimaryDark"
              fontSize={20}
              onClick={() => {
                goBack();
              }}
            />
            <Show when={props.type === "payout"}>
              <div class="flex w-full items-center justify-center gap-1 text-center">
                <p class="text-mediumBold text-textDark"> Order ID:</p>
                <p class="w-24 overflow-hidden text-ellipsis text-nowrap text-mediumBold text-textDark">
                  {props.transactionId}
                </p>
              </div>
            </Show>
            <div class="flex w-full max-w-[500px] flex-col items-stretch gap-4">
              <Show
                when={props.type === "payout" && "status" in transactionDetail}
              >
                <BankTransferPlainCard
                  transaction={transactionDetail as InitiateCoinPayoutResponse}
                />
                <Show
                  when={
                    (transactionDetail as InitiateCoinPayoutResponse).status ===
                    "PROCESSING"
                  }
                >
                  <TransferingToBank transaction={transactionDetail} />
                </Show>
              </Show>
              <Show
                when={
                  (transactionDetail as TransactionDetailV4Response)
                    .orderStatus === "COMPLETED" ||
                  (transactionDetail as TransactionDetailV4Response)
                    .orderStatus === "FAILED"
                }
              >
                <Show
                  when={
                    (transactionDetail as TransactionDetailV4Response)
                      .orderStatus === "COMPLETED"
                  }
                >
                  <p class="text-f12w400 text-center text-textNormal">
                    Tap card to view card details
                  </p>
                </Show>

                <VoucherCardPlain
                  backgroundColor={
                    (transactionDetail as TransactionDetailV4Response)
                      .brandDetails.cardBackgroundColor
                  }
                  displayText={"Card worth"}
                  showHubbleBranding={showHubbleBrandingOnVoucherCard(clientId)}
                  plainLogoUrl={
                    (transactionDetail as TransactionDetailV4Response)
                      .brandDetails.plainLogoUrl
                  }
                  value={toRupees(
                    (transactionDetail as TransactionDetailV4Response).amount
                  )}
                  class="z-[1] w-full self-center md:w-auto md:min-w-[375px]"
                  onClick={() => {
                    if (
                      (transactionDetail as TransactionDetailV4Response)
                        .orderStatus == "COMPLETED"
                    ) {
                      pushPage(
                        () => (
                          <VoucherGenSuccess
                            orderId={
                              (transactionDetail as TransactionDetailV4Response)
                                .orderId
                            }
                            transactionId={props.transactionId}
                            onClose={() => goBack()}
                            refreshProfile={false}
                          />
                        ),
                        undefined,
                        HubbleRoute.VOUCHER_GENERATION_SUCCESS
                      );
                    }
                  }}
                />
              </Show>
              <Show
                when={
                  (transactionDetail as TransactionDetailV4Response)
                    .refundStatus === "SUCCESS" ||
                  (transactionDetail as TransactionDetailV4Response)
                    .refundStatus === "FAILED" ||
                  (transactionDetail as TransactionDetailV4Response)
                    .refundStatus === "INITIATED" ||
                  (transactionDetail as TransactionDetailV4Response)
                    .refundStatus === "PENDING"
                }
              >
                <RefundStatus
                  status={
                    (transactionDetail as TransactionDetailV4Response)
                      .refundStatus
                  }
                  amount={
                    (transactionDetail as TransactionDetailV4Response)
                      .amountBreakup.paidAmount
                  }
                  timestampStr={
                    (transactionDetail as TransactionDetailV4Response)
                      .refundStatusLastUpdatedAt
                  }
                />
              </Show>
              <DonationDetailCard
                transaction={transactionDetail as TransactionDetailV4Response}
                donationDetails={detail.donationDetails}
              />
              <Show when={props.type === "payout"}>
                <AccountDetailsCard
                  transaction={transactionDetail as InitiateCoinPayoutResponse}
                />
              </Show>
              <Show when={props.type === "redemption"}>
                <ItemDetailCard
                  transaction={transactionDetail as TransactionDetailV4Response}
                />
              </Show>
              <BillDetailsCard
                transaction={transactionDetail}
                type={props.type}
              />
              <OrderDetailsCard
                transaction={transactionDetail as TransactionDetailV4Response}
                type={props.type}
              />
              <InvoiceCard
                transaction={transactionDetail as TransactionDetailV4Response}
                type={props.type}
              />
            </div>
          </div>
        );
      }}
    </ResourceLoader>
  );
}

function TransferingToBank(props: {
  transaction: TransactionDetailV4Response | InitiateCoinPayoutResponse;
}) {
  const amount =
    "amountInInr" in props.transaction
      ? props.transaction.amountInInr
      : props.transaction.amount;

  return (
    <div class="no-box-shadow primary-border flex flex-col items-stretch gap-4 rounded-2xl bg-baseTertiaryLight p-5 [box-shadow:0px_4px_4px_0px_rgba(33,_36,_39,_0.02)]">
      <span
        class="title-appbar flex items-center justify-between gap-2 border-b border-baseTertiaryMedium pb-4 text-bold text-textDark"
        onClick={() => {
          pushPage(
            () => <HighWaitingTimeBankTransfer amount={amount} />,
            {
              isModal: false,
              alignment: PageAlignment.left,
              offset: { top: 20, right: 20 },
            },
            HubbleRoute.BANK_TRANSFER_HIGH_WAITING_TIME
          );
        }}
      >
        <div class="flex flex-row items-center gap-2">
          <img src={loadingIconBlack} alt="" />
          <span class="text-medium text-textDark">
            Transfering {toRupees(amount)} to your bank account
          </span>
        </div>
        <img src={rightArrowWhite} alt="" />
      </span>
      <p class="text-medium text-textDark">
        It usually takes 2-3 hours. Don't worry full refund is assured if this
        fails
      </p>
    </div>
  );
}

function BankTransferPlainCard(props: {
  transaction: InitiateCoinPayoutResponse;
}) {
  return (
    <div class="flex flex-col items-center gap-4 self-stretch rounded-2xl bg-baseTertiaryLight p-5 [box-shadow:0px_4px_4px_0px_rgba(33,_36,_39,_0.02)]">
      <img src={bankTransfer} alt="" class="h-16 w-16" />
      <div>
        <p
          class="mb-1 text-center text-h2 text-successDark "
          classList={{
            "text-textNormal": props.transaction.status === "PROCESSING",
            "text-textDark": props.transaction.status === "SUCCESS",
          }}
        >
          {toRupees(props.transaction.amountInInr)}
        </p>
        <p
          class="text-mediumBold "
          classList={{
            "text-textNormal": props.transaction.status === "PROCESSING",
            "text-textDark": props.transaction.status === "SUCCESS",
          }}
        >
          Transferred to your bank account
        </p>
      </div>
    </div>
  );
}

function AccountDetailsCard(props: {
  transaction: InitiateCoinPayoutResponse;
}) {
  return (
    <div class="no-box-shadow primary-border flex flex-col items-stretch gap-4 rounded-2xl bg-baseTertiaryLight p-5 [box-shadow:0px_4px_4px_0px_rgba(33,_36,_39,_0.02)]">
      <span class="title-appbar flex items-center justify-between border-b border-baseTertiaryMedium pb-4 text-bold text-textDark">
        Account details
      </span>
      <div class="flex flex-col gap-4">
        <Show when={props.transaction.instrument?.mode === "UPI"}>
          <div class="body-small flex items-center justify-between text-mediumBold text-textDark">
            <span>UPI ID</span>
            <span class="text-medium text-textNormal">
              {props.transaction.instrument?.details.vpa}
            </span>
          </div>
        </Show>
        <Show when={props.transaction.instrument?.mode === "BANK"}>
          <div class="body-small flex items-center justify-between text-mediumBold text-textDark">
            <span>Name</span>
            <span class="text-medium text-textNormal">
              {props.transaction.instrument?.details.registeredName}
            </span>
          </div>
          <div class="body-small flex items-center justify-between text-mediumBold text-textDark">
            <span>Account number</span>
            <span class="text-medium text-textNormal">
              {props.transaction.instrument?.details.bankAccountNumber}
            </span>
          </div>
          <div class="body-small flex items-center justify-between text-mediumBold text-textDark">
            <span>IFSC code</span>
            <span class="text-medium text-textNormal">
              {props.transaction.instrument?.details.ifsc}
            </span>
          </div>
        </Show>
      </div>
    </div>
  );
}

function ItemDetailCard(props: { transaction: TransactionDetailV4Response }) {
  // const showSeeAllButton = props.transaction.orderStatus == "COMPLETED";
  const showLoader =
    props.transaction.orderStatus == "INITIALISED" ||
    props.transaction.orderStatus == "PROCESSING";

  return (
    <div class="no-box-shadow primary-border flex flex-col items-stretch gap-4 rounded-2xl bg-baseTertiaryLight p-5 [box-shadow:0px_4px_4px_0px_rgba(33,_36,_39,_0.02)]">
      <span class="title-appbar flex items-center justify-between border-b border-baseTertiaryMedium pb-4 text-bold text-textDark">
        Item details
        <Show when={showLoader}>
          <ThreeDotLoader color="#000" />
        </Show>
      </span>
      <div class="flex flex-col gap-4">
        <div
          class={classNames(
            "body-small flex items-center justify-between text-mediumBold text-textDark",
            "text-f12 text-textNormal"
          )}
        >
          <span>Item</span>
          <span>Quantity</span>
        </div>
        <For each={props.transaction.denominationDetails}>
          {(denomination) => (
            <div
              class={
                "body-small flex items-center justify-between text-mediumBold text-textDark"
              }
            >
              <span>
                {toRupees(denomination.denomination)}{" "}
                {props.transaction.brandDetails.brandName} card
              </span>
              <span>{denomination.quantity}</span>
            </div>
          )}
        </For>
      </div>
    </div>
  );
}

function DonationDetailCard(props: {
  transaction: TransactionDetailV4Response;
  donationDetails?: DonationDetails;
}) {
  return (
    <Show when={props.donationDetails}>
      <div class="flex flex-col gap-3 rounded-2xl border bg-baseTertiaryLight p-5">
        <div class="text-bold">Donation details</div>
        <Divider />
        <div class="flex justify-between text-mediumBold text-successDark">
          <div>
            Total donation (
            {
              props.transaction.amountBreakup.discountDetails.hubbleDiscount
                .percentage
            }
            %)
          </div>
          <div>
            {toRupees(
              props.transaction.amountBreakup.discountDetails.hubbleDiscount
                .amount
            )}
          </div>
        </div>
        <div class="text-medium">{props.donationDetails?.name}</div>
      </div>
    </Show>
  );
}

function BillDetailsCard(props: {
  transaction: TransactionDetailV4Response | InitiateCoinPayoutResponse;
  type: TransactionDetailType;
}) {
  const { data } = useClassicContext();
  const clientConfig = data.isLoggedIn ? data.clientConfig : null;
  const clientId =
    data.isLoggedIn && data.mode == "sdk" ? data.initParams.clientId : "hubble";

  function BaseDiscountNotFundedByCoins() {
    return (
      <>
        <Show
          when={
            (props.transaction as TransactionDetailV4Response).amountBreakup
              .discountDetails.hubbleDiscount.amount > 0
          }
        >
          <DiscountRow
            title={`Base discount (${
              (props.transaction as TransactionDetailV4Response).amountBreakup
                .discountDetails.hubbleDiscount.percentage
            }%) `}
            value={`-${toRupees(
              (props.transaction as TransactionDetailV4Response).amountBreakup
                .discountDetails.hubbleDiscount.amount
            )}`}
          />
        </Show>
        <Show
          when={
            (props.transaction as TransactionDetailV4Response).amountBreakup
              .discountDetails.coinDiscount?.amount ?? 0 > 0
          }
        >
          <DiscountRow
            title={`${getCoinLabelCapitalizedPluralized(clientId)} discount (${
              (props.transaction as TransactionDetailV4Response).amountBreakup
                .discountDetails.coinDiscount!.percentage
            }%)`}
            value={`-${toRupees(
              (props.transaction as TransactionDetailV4Response).amountBreakup
                .discountDetails.coinDiscount!.amount
            )}`}
          />
        </Show>
      </>
    );
  }

  return (
    <div class="no-box-shadow  primary-border flex flex-col items-stretch rounded-2xl bg-baseTertiaryLight p-5 [box-shadow:0px_4px_4px_0px_rgba(33,_36,_39,_0.02)]">
      <span class="title-appbar flex  items-center justify-between  border-b border-baseTertiaryMedium pb-4 text-bold text-textDark">
        Bill details
      </span>
      <div class="flex flex-col gap-3">
        <Switch>
          <Match when={props.type === "payout"}>
            <div class="title-secondary flex flex-row justify-between px-0 py-3 text-mediumBold text-textDark">
              <span>Transfer amount</span>
              <span>
                {toRupees(
                  (props.transaction as InitiateCoinPayoutResponse)
                    .amountInInr ?? 0
                )}
              </span>
            </div>
          </Match>
          <Match when={props.type === "redemption"}>
            <div class="title-secondary flex flex-row justify-between  px-0 pt-3 text-mediumBold text-textDark">
              <span>Brand card worth</span>
              <span>
                {toRupees(
                  (props.transaction as TransactionDetailV4Response).amount ?? 0
                )}
              </span>
            </div>
            <Show
              fallback={<BaseDiscountNotFundedByCoins />}
              when={
                (props.transaction as TransactionDetailV4Response)
                  .useCoinsToFundBaseDiscount
              }
            >
              <CoinsWhenUseCoinsToFundBaseDiscount
                transaction={props.transaction as TransactionDetailV4Response}
              />
            </Show>

            <Show
              when={
                (props.transaction as TransactionDetailV4Response).amountBreakup
                  .discountDetails.couponDiscount?.amount ?? 0 > 0
              }
            >
              <DiscountRow
                title={`Coupon discount (${
                  (props.transaction as TransactionDetailV4Response)
                    .amountBreakup.discountDetails.code
                })`}
                value={`-${toRupees(
                  (props.transaction as TransactionDetailV4Response)
                    .amountBreakup.discountDetails.couponDiscount!.amount
                )}`}
              />
            </Show>
          </Match>
        </Switch>
        <Show when={clientConfig?.payoutsEnabled}>
          <div class="flex flex-row items-start justify-between px-0 py-1.5 text-bold text-textDark">
            <div>
              <p class="text-mediumBold">Taxes</p>
              <p class="text-f12 text-textNormal">194(R) adjusted</p>
            </div>
            <p class="text-mediumBold text-textDark">
              <Show
                when={props.type === "payout"}
                fallback={toRupees(
                  (props.transaction as TransactionDetailV4Response)
                    .amountBreakup.additionalCharges?.[0]?.tdsCharges.amount ??
                    0
                )}
              >
                {toRupees(
                  (props.transaction as InitiateCoinPayoutResponse).metadata
                    .tdsChargesInPartnerCurrency ?? 0
                )}
              </Show>
            </p>
          </div>
        </Show>
        <Divider />
        <div class="flex items-center justify-between text-bold text-textDark">
          <span
            class="title-secondary flex flex-col gap-1  text-textDark"
            classList={{
              "text-mediumSemiBold": props.type === "redemption",
              "text-mediumBold": props.type === "payout",
            }}
          >
            <Show
              when={props.type === "payout"}
              fallback={
                <>
                  You paid
                  <span class="text-f12 text-textNormal">
                    {
                      (props.transaction as TransactionDetailV4Response)
                        .paymentMode
                    }
                  </span>
                </>
              }
            >
              Total incentive utilisation
            </Show>
          </span>
          <span
            class=""
            classList={{
              "title-secondary flex items-center gap-0.5 text-bold text-coinPercentageOrange":
                props.type === "payout",
            }}
          >
            <Switch>
              <Match
                when={
                  props.type === "redemption" && !clientConfig?.payoutsEnabled
                }
              >
                {toRupees(
                  (props.transaction as TransactionDetailV4Response)
                    .amountBreakup.paidAmount
                )}
              </Match>
              <Match
                when={
                  props.type === "redemption" && clientConfig?.payoutsEnabled
                }
              >
                {toRupees(
                  (props.transaction as TransactionDetailV4Response)
                    .coinsDebited ?? 0
                )}
              </Match>
              <Match when={props.type === "payout"}>
                <img src={defaultCoin} class="h-4 w-4" alt="" />
                {toRupees(
                  (props.transaction as InitiateCoinPayoutResponse)
                    .amountInPartnerCurrency
                )}
              </Match>
            </Switch>
          </span>
        </div>
      </div>
    </div>
  );
}

type CoinsWhenUseCoinsToFundBaseDiscountProps = {
  transaction: TransactionDetailV4Response;
};

function CoinsWhenUseCoinsToFundBaseDiscount(
  props: CoinsWhenUseCoinsToFundBaseDiscountProps
) {
  const { data } = useClassicContext();
  const clientId =
    data.isLoggedIn && data.mode == "sdk" ? data.initParams.clientId : "hubble";
  const coinIconUrl = data.isLoggedIn
    ? data.clientConfig.coinIconUrl
    : defaultCoin;

  const coinToInrConversionRatio =
    props.transaction.coinToInrConversionRatio ?? 1;

  const coinLabel = getCoinLabelCapitalized(clientId);
  const coinLabelPluralized = getCoinLabelCapitalizedPluralized(clientId);

  const discountAmount =
    props.transaction.amountBreakup.discountDetails.hubbleDiscount.amount;
  const coinsUsed =
    props.transaction.coinsDebited ?? discountAmount / coinToInrConversionRatio;
  return (
    <div class="flex flex-col">
      <div class="space-between flex flex-row justify-between py-1 text-mediumBolder text-successDark">
        <span class="flex flex-row items-center gap-1">
          {coinLabel} discount (
          <img src={coinIconUrl} class="h-4 w-4" alt="coin icon" /> {coinsUsed})
        </span>
        <span>-{toRupees(discountAmount)}</span>
      </div>

      <div class="flex flex-row items-center gap-1 text-medium text-textNormal">
        <img src={coinIconUrl} class="h-4 w-4" alt="coin icon" />{" "}
        {1 / coinToInrConversionRatio} {coinLabelPluralized} = ₹1
      </div>
    </div>
  );
}

function OrderDetailsCard(props: {
  transaction: TransactionDetailV4Response | InitiateCoinPayoutResponse;
  type: TransactionDetailType;
}) {
  return (
    <div class="no-box-shadow primary-border flex flex-col items-stretch gap-3 rounded-2xl bg-baseTertiaryLight p-5 [box-shadow:0px_4px_4px_0px_rgba(33,_36,_39,_0.02)]">
      <span class="title-appbar flex items-center justify-between border-b border-baseTertiaryMedium pb-4 text-bold text-textDark">
        Order details
      </span>
      <div class="flex flex-col gap-[14px]">
        <div
          class="flex flex-col items-stretch justify-between gap-1 text-medium text-textNormal"
          onClick={async () => {
            if (props.type === "payout") {
              await copyText({
                text: (props.transaction as InitiateCoinPayoutResponse).metadata
                  .payoutInstrumentId,
              });
            } else if (props.type === "redemption") {
              await copyText({
                text: (props.transaction as TransactionDetailV4Response)
                  .orderId,
              });
            }
          }}
        >
          <span class="title-secondary text-mediumSemiBold text-textDark">
            Order ID
          </span>
          <span class="body-small flex items-center justify-between text-[13px]">
            <Show
              when={props.type === "redemption"}
              fallback={
                (props.transaction as InitiateCoinPayoutResponse).metadata
                  .payoutInstrumentId
              }
            >
              {(props.transaction as TransactionDetailV4Response).orderId}
            </Show>{" "}
            <PhosphorIcon name="copy-simple" />
          </span>
        </div>
        <div
          class="flex flex-col items-stretch justify-between gap-1 text-medium text-textNormal"
          onClick={async () => {
            await copyText({
              text: props.transaction.id,
            });
          }}
        >
          <span class="title-secondary text-mediumSemiBold text-textDark">
            Transaction ID
          </span>
          <span class="body-small flex items-center justify-between text-[13px]">
            {props.transaction.id} <PhosphorIcon name="copy-simple" />
          </span>
        </div>
        <div class="flex flex-col items-stretch justify-between gap-1 text-medium text-textNormal">
          <span class="title-secondary text-mediumSemiBold text-textDark">
            <Show when={props.type === "redemption"} fallback={"Transfer date"}>
              Purchase date
            </Show>
          </span>
          <span class="body-small flex items-center justify-between text-[13px]">
            <Show
              when={props.type === "redemption"}
              fallback={formatDate(
                new Date(
                  (props.transaction as InitiateCoinPayoutResponse).transactedAt
                )
              )}
            >
              {formatDate(
                new Date(
                  (props.transaction as TransactionDetailV4Response).dateTime
                )
              )}
            </Show>
          </span>
        </div>
      </div>
    </div>
  );
}

function InvoiceCard(props: {
  transaction: TransactionDetailV4Response;
  type: TransactionDetailType;
}) {
  return (
    <div class="no-box-shadow primary-border flex flex-col items-stretch rounded-2xl bg-baseTertiaryLight px-5 py-0 [box-shadow:0px_4px_4px_0px_rgba(33,_36,_39,_0.02)]">
      <Show when={props.type === "redemption"}>
        <div
          class="flex cursor-pointer gap-1.5 border-t border-baseTertiaryMedium px-0 py-4 first:border-t-0"
          onClick={() => {
            pushPage(
              () => <InvoicePage url={props.transaction.receiptLink} />,
              {
                isModal: true,
              },
              HubbleRoute.INVOICE
            );
          }}
        >
          <PhosphorIcon name="file-text" />
          <span class="title-secondary flex items-center justify-between text-[13px]">
            View invoice
          </span>
        </div>
      </Show>

      <div
        class="flex cursor-pointer gap-1.5 border-t border-baseTertiaryMedium px-0 py-4 first:border-t-0"
        onClick={openIntercom}
      >
        <PhosphorIcon name="chat-dots" />
        <span class="title-secondary flex items-center justify-between text-[13px]">
          Contact support
        </span>
      </div>
    </div>
  );
}

function InvoicePage(props: { url: string }) {
  return (
    <div class="rounded-bl-0 rounded-br-0 h-[80vh] rounded-tl-2xl rounded-tr-2xl bg-baseTertiaryLight">
      <iframe class="h-full w-full border-none" src={props.url} />
    </div>
  );
}

/// format date to 12 Jun 2023 · 4:00 PM
function formatDate(date: Date) {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
  };
  return date.toLocaleDateString("en-US", options);
}

type RefundSuccessfulProps = {
  amount: number;
  timestampStr: string;
  status: RefundStatusType;
};

function RefundStatus(props: RefundSuccessfulProps) {
  const formattedDateTime = formatDate(new Date(props.timestampStr));

  return (
    <div class="flex flex-col rounded-2xl bg-baseTertiaryLight p-5">
      <div class="flex flex-row justify-between">
        <div class="inline-flex flex-row items-center gap-1">
          <Switch>
            <Match when={props.status == "SUCCESS"}>
              <PhosphorIcon
                class="text-successDark"
                name="check"
                size="bold"
                fontSize={13}
              />

              <span class="text-mediumBold text-successDark">
                Refund successful
              </span>
            </Match>
            <Match when={true}>
              <span class="text-mediumBold text-successDark">
                Refund initiated
              </span>
            </Match>
          </Switch>
        </div>

        <div class="body-small text-f12 text-textNormal">
          {formattedDateTime}
        </div>
      </div>

      <Spacer height={16} />
      <Divider />
      <Spacer height={12} />
      <div class="text-medium text-textDark">
        <Switch>
          <Match when={props.status == "SUCCESS"}>
            ₹{props.amount} credit back to your source account
          </Match>
          <Match when={true}>
            Takes around 3-5 business days for refund amount to credit back to
            your source account
          </Match>
        </Switch>
      </div>
    </div>
  );
}

function DiscountRow(props: { title: string; value: string }) {
  return (
    <div class="flex flex-row justify-between px-0 text-mediumBold text-textDark">
      <span>{props.title}</span>
      <span>{props.value}</span>
    </div>
  );
}
